import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const ERP_HERO_PRODUCT_ASSET =
  "/assets/products/erp/hero/itaas-erp-product-dashboard.png";
export const ERP_HERO_HUMAN_ASSET =
  "/assets/products/erp/hero/itaas-erp-executive.png?v=1";

/** Editorial grounded in catalog SoT — ERP foundation, not full advanced accounting. */
export const ERP_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction générale",
    titleEn: "Executive leadership",
    bodyFr: "Pilotez organisation, sites et indicateurs depuis un socle ERP unique.",
    bodyEn: "Steer organization, sites and indicators from one ERP foundation.",
  },
  {
    id: "finance",
    icon: "💼",
    titleFr: "Finance opérationnelle",
    titleEn: "Operational finance",
    bodyFr: "Suivez factures et finance opérationnelle sans prétendre à une compta générale avancée.",
    bodyEn: "Track invoices and operational finance without claiming advanced general ledger.",
  },
  {
    id: "procurement",
    icon: "🛒",
    titleFr: "Achats",
    titleEn: "Procurement",
    bodyFr: "Gérez achats et fournisseurs dans le même fil que stock et organisation.",
    bodyEn: "Run purchasing and suppliers in the same thread as stock and organization.",
  },
  {
    id: "ops",
    icon: "📦",
    titleFr: "Opérations & stock",
    titleEn: "Operations & stock",
    bodyFr: "Inventaire intégré, mouvements et historique pour synchroniser avec Inventory.",
    bodyEn: "Integrated inventory, movements and history — ready to sync with Inventory.",
  },
  {
    id: "admin",
    icon: "🗂️",
    titleFr: "Administration",
    titleEn: "Administration",
    bodyFr: "Organisation multi-sites, départements, utilisateurs, rôles et journal d’audit.",
    bodyEn: "Multi-site organization, departments, users, roles and audit journal.",
  },
];

export const ERP_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "sme",
    icon: "📈",
    titleFr: "ERP accessible aux PME",
    titleEn: "ERP accessible to SMEs",
    bodyFr: "Socle modulaire sans la complexité d’un ERP enterprise monolitique.",
    bodyEn: "Modular foundation without monolithic enterprise ERP complexity.",
  },
  {
    id: "org",
    icon: "🏢",
    titleFr: "Organisation structurée",
    titleEn: "Structured organization",
    bodyFr: "Sites, départements et workflows par organisation — données isolées.",
    bodyEn: "Sites, departments and workflows per organization — isolated data.",
  },
  {
    id: "ops",
    icon: "🔗",
    titleFr: "Achats · finance · stock",
    titleEn: "Purchasing · finance · stock",
    bodyFr: "Base solide pour orchestrer achats, finance opérationnelle et inventaire.",
    bodyEn: "Solid base to orchestrate purchasing, operational finance and inventory.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Évolutif Suite ITaaS",
    titleEn: "Extensible ITaaS Suite",
    bodyFr: "S’étend vers modules verticaux et supply chain (Inventory, MinePortal…).",
    bodyEn: "Extends toward vertical modules and supply chain (Inventory, MinePortal…).",
  },
];

export const ERP_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Organisation",
    titleEn: "Organization",
    features: [
      "Organisation multi-sites et départements",
      "Gestion des utilisateurs et rôles",
      "Journal d’audit et traçabilité",
    ],
  },
  {
    titleFr: "Achats & finance",
    titleEn: "Purchasing & finance",
    features: [
      "Achats et fournisseurs",
      "Finance opérationnelle et factures",
    ],
  },
  {
    titleFr: "Stock",
    titleEn: "Stock",
    features: [
      "Module inventaire intégré",
      "Mouvements de stock et historique",
    ],
  },
  {
    titleFr: "Plateforme",
    titleEn: "Platform",
    features: [
      "Architecture modulaire extensible",
      "Notifications in-app",
      "Interface web moderne",
    ],
  },
];

const ERP_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "modular",
    labelFr: "ERP modulaire Suite ITaaS",
    labelEn: "Modular ITaaS Suite ERP",
  },
  {
    id: "sme",
    labelFr: "Conçu pour PME et opérations africaines",
    labelEn: "Built for SMEs and African operations",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

export const ERP_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: ERP_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS ERP — pilotage organisation, achats et opérations",
    en: "ITaaS ERP dashboard — organization, purchasing and operations overview",
  },
  humanImageSrc: ERP_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Dirigeant consultant une tablette pour analyser les indicateurs de l’entreprise",
    en: "Executive reviewing a tablet to analyze company indicators",
  },
  visualCaption: {
    fr: "Interface produit ITaaS ERP — tableaux de bord décisionnels",
    en: "ITaaS ERP product interface — decision dashboards",
  },
  heroKicker: {
    fr: "Organisation · Achats · Finance · Stock · Multi-sites",
    en: "Organization · Purchasing · Finance · Stock · Multi-site",
  },
  audiences: ERP_AUDIENCES,
  audienceTitle: {
    fr: "Un socle ERP pour chaque acteur de l’entreprise",
    en: "An ERP foundation for every stakeholder in the business",
  },
  outcomes: ERP_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour structurer organisation, achats et opérations.",
    en: "Practical benefits for structuring organization, purchasing and operations.",
  },
  capabilityGroups: ERP_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Socle ERP pour entreprises africaines",
    en: "ERP foundation for African businesses",
  },
  contextLead: {
    fr: "Modulaire et évolutif — sans promettre comptabilité générale avancée, CRM intégré ou BI consolidé tant qu’ils restent en roadmap.",
    en: "Modular and extensible — without claiming advanced GL, integrated CRM or consolidated BI while they remain on the roadmap.",
  },
  contextFallbackPoints: [
    {
      fr: "Organisation multi-sites, rôles et audit",
      en: "Multi-site organization, roles and audit",
    },
    {
      fr: "Achats, finance opérationnelle et inventaire",
      en: "Purchasing, operational finance and inventory",
    },
    {
      fr: "Base pour orchestrer les modules Suite ITaaS",
      en: "Foundation to orchestrate ITaaS Suite modules",
    },
  ],
  trustBaseChips: ERP_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
  ctaPriority: "demo",
};
